import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, Star } from "lucide-react";

/**
 * Modal for a teacher to submit or edit the end-of-session report.
 * Props:
 *   session: full session object (may include existing session.report)
 *   onClose: () => void
 *   onSaved: (updatedReport) => void
 */
export default function SessionReportModal({ session, onClose, onSaved }) {
  const existing = session?.report || {};
  const [topic, setTopic] = useState(existing.topic_taught || "");
  const [exercises, setExercises] = useState(existing.exercises_completed || "");
  const [homework, setHomework] = useState(existing.homework_assigned || "");
  const [strengths, setStrengths] = useState(existing.strengths || "");
  const [weaknesses, setWeaknesses] = useState(existing.weaknesses || "");
  const [nextTopic, setNextTopic] = useState(existing.next_topic || "");
  const [rating, setRating] = useState(existing.performance_rating || 4);
  const [notes, setNotes] = useState(existing.teacher_notes || "");
  const [busy, setBusy] = useState(false);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!topic.trim()) return toast.error("Topic taught is required");
    setBusy(true);
    try {
      const { data } = await api.post(`/api/centre/sessions/${session.id}/report`, {
        topic_taught: topic.trim(),
        exercises_completed: exercises,
        homework_assigned: homework,
        strengths, weaknesses,
        next_topic: nextTopic,
        performance_rating: Number(rating),
        teacher_notes: notes,
      });
      toast.success(existing.topic_taught ? "Report updated" : "Report submitted");
      onSaved?.(data.report);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
         data-testid="session-report-modal">
      <div className="bg-white w-full max-w-2xl rounded-2xl border border-[#E5E7EB] max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB] sticky top-0 bg-white">
          <div>
            <div className="label-eyebrow">Session Report</div>
            <div className="font-display font-bold text-lg mt-0.5">
              {session.student_name} · {session.subject || "General"}
            </div>
            <div className="text-xs text-[#4B5563]">
              {new Date(session.start_at).toLocaleString("en-CA")}
            </div>
          </div>
          <button onClick={onClose} data-testid="close-report-modal"
            className="p-2 rounded-md hover:bg-[#F3F4F6]"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4" data-testid="session-report-form">
          <TextField label="Topic taught*" value={topic} setValue={setTopic}
            testid="rep-topic" required />
          <TextArea label="Exercises completed" value={exercises} setValue={setExercises}
            testid="rep-exercises" placeholder="e.g. Chapter 4 pp. 12-14, worksheet A" />
          <TextArea label="Homework assigned" value={homework} setValue={setHomework}
            testid="rep-homework" placeholder="e.g. Chapter 4 p. 15 Q1-Q5, due next session" />
          <div className="grid md:grid-cols-2 gap-4">
            <TextArea label="Strengths observed" value={strengths} setValue={setStrengths}
              testid="rep-strengths" rows={2} />
            <TextArea label="Areas to work on" value={weaknesses} setValue={setWeaknesses}
              testid="rep-weaknesses" rows={2} />
          </div>
          <TextField label="Next topic (plan)" value={nextTopic} setValue={setNextTopic}
            testid="rep-next" />

          <div>
            <label className="text-sm font-semibold block mb-2">Performance rating</label>
            <div className="flex gap-1" data-testid="rep-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setRating(n)}
                  data-testid={`rep-star-${n}`}
                  className={`p-1 rounded ${n <= rating ? "text-[#D92D20]" : "text-[#D1D5DB]"} hover:scale-110 transition-transform`}>
                  <Star className={`w-7 h-7 ${n <= rating ? "fill-[#D92D20]" : ""}`} />
                </button>
              ))}
              <span className="ml-2 text-sm font-semibold self-center">{rating} / 5</span>
            </div>
          </div>

          <TextArea label="Private teacher notes (optional)" value={notes} setValue={setNotes}
            testid="rep-notes" placeholder="Not shown to the parent" />

          <div className="pt-2 flex items-center justify-between border-t border-[#E5E7EB]">
            <button type="button" onClick={onClose} data-testid="rep-cancel"
              className="text-sm font-semibold text-[#4B5563]">Cancel</button>
            <button type="submit" disabled={busy} data-testid="rep-submit"
              className="btn-primary">
              {busy ? "Saving..." : existing.topic_taught ? "Update Report" : "Submit Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TextField({ label, value, setValue, testid, required, placeholder }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <input data-testid={testid} required={required} placeholder={placeholder}
        value={value} onChange={(e) => setValue(e.target.value)}
        className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
    </div>
  );
}

function TextArea({ label, value, setValue, testid, placeholder, rows = 3 }) {
  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      <textarea data-testid={testid} rows={rows} placeholder={placeholder}
        value={value} onChange={(e) => setValue(e.target.value)}
        className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
    </div>
  );
}
